import { Switch } from "@headlessui/react";
import { isEmpty, map } from "lodash";
import React, { useMemo } from "react";
import CenteredLoading from "../../../../components/CenteredLoading";
import { useLoading } from "../../../../context/loading";
import { LoadingKey } from "../../../../context/loading/LoadingProvider";
import {
  NotificationSubscriptionKey,
  notificationSubscriptionOptions,
  useNotificationSubscriptions
} from "../../../../context/notificationSubscriptions/NotificationSubscriptionsProvider";
import classNames from "../../../../utils/classNames";

interface Props {}

const NotificationSubscriptionsPage: React.FC<Props> = () => {
  const { getIsReady } = useLoading();
  const { notificationSubscriptions, toggleNotificationSubscription } =
    useNotificationSubscriptions();

  const isLoadingNotifications = useMemo(
    () =>
      isEmpty(notificationSubscriptions) &&
      !getIsReady([LoadingKey.workspaceNotificationSubscriptions]),
    [notificationSubscriptions, getIsReady]
  );

  return (
    <div className="flex flex-col w-full">
      <div className="mb-6">
        <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
          Notifications
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          Configure your personal email notifications for this workspace below.
        </p>
      </div>
      <div>
        {isLoadingNotifications ? (
          <CenteredLoading className="h-96" />
        ) : (
          <>
            {map(
              notificationSubscriptions,
              (notificationSubscription, key: NotificationSubscriptionKey) => {
                const description = notificationSubscriptionOptions[key];

                if (!description) return;

                const { title, subtitle } = description;

                return (
                  <Switch.Group
                    as="li"
                    className="py-4 flex items-center justify-between"
                    key={key}
                  >
                    <div className="flex flex-col w-8/12">
                      <Switch.Label
                        as="div"
                        className="text-sm font-medium mb-1 text-gray-900 dark:text-white"
                        passive
                      >
                        {title}
                      </Switch.Label>
                      <Switch.Description className="text-sm text-gray-500 dark:text-gray-200">
                        {subtitle}
                      </Switch.Description>
                    </div>
                    <Switch
                      checked={notificationSubscription.subscribed}
                      onChange={() => {
                        toggleNotificationSubscription(
                          notificationSubscription
                        );
                      }}
                      className={classNames(
                        notificationSubscription.subscribed
                          ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                          : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400",
                        "ml-4 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-hotspotty-400"
                      )}
                    >
                      <span className="sr-only">{title}</span>
                      <span
                        aria-hidden="true"
                        className={classNames(
                          notificationSubscription.subscribed
                            ? "translate-x-5"
                            : "translate-x-0",
                          "inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200"
                        )}
                      />
                    </Switch>
                  </Switch.Group>
                );
              }
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSubscriptionsPage;
